/**
 * Client-side citation formatter.
 *
 * Pragmatic 80/20 approach covering APA, MLA, IEEE, and Chicago styles.
 * Not a full CSL engine — handles the common article/book patterns that
 * cover the vast majority of academic use cases.
 */

import type { BibEntry } from "../stores/citation";

export type CitationStyle = "apa" | "mla" | "ieee" | "chicago";

export const CITATION_STYLES: { id: CitationStyle; label: string }[] = [
  { id: "apa", label: "APA" },
  { id: "mla", label: "MLA" },
  { id: "ieee", label: "IEEE" },
  { id: "chicago", label: "Chicago" },
];

// ── Author helpers ────────────────────────────────────────────────

function lastFirst(name: string): { last: string; initials: string; full: string } {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return { last: parts[0], initials: "", full: parts[0] };
  const last = parts[parts.length - 1];
  const initials = parts.slice(0, -1).map((p) => p[0]?.toUpperCase() + ".").join(" ");
  return { last, initials, full: name.trim() };
}

function formatAuthorsAPA(authors: string[]): string {
  if (authors.length === 0) return "";
  const formatted = authors.map((a) => {
    const { last, initials } = lastFirst(a);
    return initials ? `${last}, ${initials}` : last;
  });
  if (formatted.length === 1) return formatted[0];
  if (formatted.length === 2) return `${formatted[0]}, & ${formatted[1]}`;
  return `${formatted.slice(0, -1).join(", ")}, & ${formatted[formatted.length - 1]}`;
}

function formatAuthorsMLA(authors: string[]): string {
  if (authors.length === 0) return "";
  const first = lastFirst(authors[0]);
  const firstStr = first.initials
    ? `${first.last}, ${first.full.split(/\s+/).slice(0, -1).join(" ")}`
    : first.last;
  if (authors.length === 1) return firstStr;
  if (authors.length === 2) return `${firstStr}, and ${authors[1]}`;
  return `${firstStr}, et al.`;
}

function formatAuthorsIEEE(authors: string[]): string {
  if (authors.length === 0) return "";
  const formatted = authors.map((a) => {
    const { last, initials } = lastFirst(a);
    return initials ? `${initials} ${last}` : last;
  });
  if (formatted.length <= 3) return formatted.join(", ");
  return `${formatted[0]} et al.`;
}

function formatAuthorsChicago(authors: string[]): string {
  if (authors.length === 0) return "";
  const first = lastFirst(authors[0]);
  const firstStr = first.initials
    ? `${first.last}, ${first.full.split(/\s+/).slice(0, -1).join(" ")}`
    : first.last;
  if (authors.length === 1) return firstStr;
  if (authors.length === 2) return `${firstStr} and ${authors[1]}`;
  return `${firstStr} et al.`;
}

// ── Full reference formatters ─────────────────────────────────────

function formatAPA(entry: BibEntry): string {
  const parts: string[] = [];
  if (entry.authors.length > 0) parts.push(formatAuthorsAPA(entry.authors));
  if (entry.year) parts.push(`(${entry.year})`);
  parts.push(`${entry.title}.`);
  if (entry.journal) parts.push(`${entry.journal}.`);
  if (entry.doi) parts.push(`https://doi.org/${entry.doi}`);
  return parts.join(" ");
}

function formatMLA(entry: BibEntry): string {
  const parts: string[] = [];
  if (entry.authors.length > 0) parts.push(`${formatAuthorsMLA(entry.authors)}.`);
  parts.push(`"${entry.title}."`);
  if (entry.journal) parts.push(`${entry.journal},`);
  if (entry.year) parts.push(`${entry.year}.`);
  return parts.join(" ");
}

function formatIEEE(entry: BibEntry): string {
  const parts: string[] = [];
  if (entry.authors.length > 0) parts.push(`${formatAuthorsIEEE(entry.authors)},`);
  parts.push(`"${entry.title},"`);
  if (entry.journal) parts.push(`${entry.journal},`);
  if (entry.year) parts.push(`${entry.year}.`);
  return parts.join(" ");
}

function formatChicago(entry: BibEntry): string {
  const parts: string[] = [];
  if (entry.authors.length > 0) parts.push(`${formatAuthorsChicago(entry.authors)}.`);
  parts.push(`"${entry.title}."`);
  if (entry.journal) parts.push(`${entry.journal}`);
  if (entry.year) parts.push(`(${entry.year}).`);
  return parts.join(" ");
}

// ── Public API ────────────────────────────────────────────────────

export function formatCitation(entry: BibEntry, style: CitationStyle): string {
  switch (style) {
    case "apa": return formatAPA(entry);
    case "mla": return formatMLA(entry);
    case "ieee": return formatIEEE(entry);
    case "chicago": return formatChicago(entry);
  }
}

/**
 * Inline citation reference (parenthetical / bracketed form).
 * `index` is used for IEEE numbered style.
 */
export function formatInlineRef(
  entry: BibEntry,
  style: CitationStyle,
  index?: number,
): string {
  const firstAuthor = entry.authors.length > 0
    ? lastFirst(entry.authors[0]).last
    : entry.key;
  const etAl = entry.authors.length > 2 ? " et al." : "";
  const year = entry.year ?? "n.d.";

  switch (style) {
    case "apa":
      return `(${firstAuthor}${etAl}, ${year})`;
    case "mla":
      return `(${firstAuthor}${etAl} ${entry.year ? year : ""})`.replace(/\s+\)/, ")");
    case "ieee":
      return `[${index != null ? index + 1 : "?"}]`;
    case "chicago":
      return `(${firstAuthor}${etAl} ${year})`;
  }
}
